import React, { useCallback, useState } from 'react';
import { ActionIcon, Badge, Button, Text } from '@mantine/core';
import { IconExternalLink, IconSparkles, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useGuardedFetch, useLatestRequest } from 'hooks/useGuardedFetch';
import { CORE_NS } from 'recruiter-i18n/namespaces/core';
import { SHELL_NS } from 'recruiter-i18n/namespaces/shell';
import { displayLocale } from 'utils/appLocale';
import { openWhatsNewDocs } from 'utils/openDocsPage';

import {
  formatUpdateDateRange,
  utcDateOnly,
} from '../../productUpdates/productUpdateDates';
import { PRODUCT_UPDATES } from '../../productUpdates/productUpdates';
import type { ProductUpdate } from '../../productUpdates/parseProductUpdates';
import {
  productUpdateCopy,
  productUpdateTag,
  type WhatsNewText,
} from '../../productUpdates/productUpdateText';
import {
  selectWhatsNewNotice,
  whatsNewDocPath,
  whatsNewTags,
  type WhatsNewMemory,
  type WhatsNewNotice,
} from '../../productUpdates/selectWhatsNewNotice';
import { whatsNewClient, type WhatsNewStateClient } from './whatsNewClient';
import styles from './WhatsNewPopup.module.css';

const TAG_COLORS = ['teal', 'green', 'pink', 'violet'] as const;

type WhatsNewPopupProps = {
  /** Defaults to the shipped updates file. Tests pass a fixture list. */
  updates?: readonly ProductUpdate[];
  /** Defaults to the time the card mounted. Tests pin a UTC day. */
  now?: Date;
  /** Defaults to the account's saved state. Tests pass a fake client. */
  client?: WhatsNewStateClient;
};

function NoticeCopy({
  notice,
  t,
}: {
  notice: WhatsNewNotice;
  t: WhatsNewText;
}) {
  const update = notice.updates[0];
  if (notice.kind === 'single' && update) {
    const copy = productUpdateCopy(update, t);
    return (
      <>
        <Text className={styles.title}>{copy.title}</Text>
        <Text className={styles.summary}>{copy.summary}</Text>
      </>
    );
  }
  return (
    <ul className={styles.updates}>
      {notice.updates.map((item) => {
        const copy = productUpdateCopy(item, t);
        return (
          <li key={item.id}>
            <Text className={styles.updateTitle}>{copy.title}</Text>
            <Text className={styles.updateSummary}>{copy.summary}</Text>
          </li>
        );
      })}
    </ul>
  );
}

export const WhatsNewPopup = ({
  updates = PRODUCT_UPDATES,
  now,
  client = whatsNewClient,
}: WhatsNewPopupProps) => {
  const { t } = useTranslation([SHELL_NS, CORE_NS]);
  const [mountedAt] = useState(() => new Date());
  const clock = now ?? mountedAt;
  const today = utcDateOnly(clock);
  const [memory, setMemory] = useState<WhatsNewMemory | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [dismissFailed, setDismissFailed] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const beginDismiss = useLatestRequest();

  useGuardedFetch(
    async (isStale) => {
      void refreshKey;
      setLoadError(false);
      try {
        const next = await client.load();
        if (isStale()) return;
        setMemory(next);
      } catch {
        if (isStale()) return;
        setLoadError(true);
      }
    },
    [client, refreshKey]
  );

  const notice = memory ? selectWhatsNewNotice(updates, memory, today) : null;

  const dismiss = useCallback(() => {
    if (!notice || !memory) return;
    const ids = notice.updates.map((update) => update.id);
    const previous = memory;
    const isStale = beginDismiss();
    setDismissFailed(false);
    setMemory({
      firstOpenedOn: memory.firstOpenedOn,
      dismissedIds: new Set([...memory.dismissedIds, ...ids]),
    });
    void client.dismiss(ids).then(
      (next) => {
        if (isStale()) return;
        setMemory(next);
      },
      () => {
        if (isStale()) return;
        setMemory(previous);
        setDismissFailed(true);
      }
    );
  }, [beginDismiss, client, memory, notice]);

  const readUpdate = useCallback(() => {
    if (!notice) return;
    void openWhatsNewDocs(whatsNewDocPath(notice));
  }, [notice]);

  if (loadError) {
    return (
      <section className={styles.card} aria-label={t('whatsNew.eyebrow')}>
        <Text className={styles.summary}>{t('whatsNew.loadError')}</Text>
        <Button
          fullWidth
          color='dark'
          className={styles.cta}
          onClick={() => setRefreshKey((key) => key + 1)}
        >
          {t('actions.retry', { ns: CORE_NS })}
        </Button>
      </section>
    );
  }

  if (!notice || !memory) return null;

  const newestUpdate = notice.updates[0];
  const oldestUpdate = notice.updates[notice.updates.length - 1];
  if (!newestUpdate || !oldestUpdate) return null;
  const tags = whatsNewTags(notice);
  const ctaKey =
    notice.kind === 'single'
      ? 'whatsNew.readFullUpdate'
      : 'whatsNew.readChangelog';

  return (
    <section className={styles.card} aria-label={t('whatsNew.eyebrow')}>
      <div className={styles.header}>
        <div className={styles.eyebrow}>
          <IconSparkles size={14} className={styles.sparkle} />
          <span>{t('whatsNew.eyebrow')}</span>
          <span aria-hidden='true'>·</span>
          <span className={styles.date}>
            {formatUpdateDateRange(
              newestUpdate.publishedOn,
              oldestUpdate.publishedOn,
              displayLocale()
            )}
          </span>
        </div>
        <ActionIcon
          variant='subtle'
          color='gray'
          aria-label={t('whatsNew.dismiss')}
          onClick={dismiss}
        >
          <IconX size={16} />
        </ActionIcon>
      </div>
      <NoticeCopy notice={notice} t={t} />
      {dismissFailed && (
        <Text className={styles.summary}>{t('whatsNew.dismissError')}</Text>
      )}
      {tags.length > 0 && (
        <div className={styles.tags}>
          {tags.map((tag, index) => (
            <Badge
              key={tag}
              variant='light'
              color={TAG_COLORS[index % TAG_COLORS.length]}
              radius='xl'
            >
              {productUpdateTag(tag, t)}
            </Badge>
          ))}
        </div>
      )}
      <Button
        fullWidth
        color='dark'
        className={styles.cta}
        rightSection={<IconExternalLink size={16} />}
        onClick={readUpdate}
      >
        {t(ctaKey)}
      </Button>
    </section>
  );
};
